import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { GradientButton } from '../components/GradientButton';
import { Check, FileText, Mail, SlidersHorizontal, Upload } from '../components/icons';
import { FeatureHeader } from '../components/FeatureHeader';
import { ScreenHeader } from '../components/ScreenHeader';
import { FeedbackWidget } from '../components/FeedbackWidget';
import { OutputLanguagePicker } from '../components/OutputLanguagePicker';
import { PiedAction } from '../components/PiedAction';
import { SectionRepliable } from '../components/SectionRepliable';
import { useClaude } from '../hooks/useClaude';
import { useGabarit } from '../hooks/useGabarit';
import { useLetterDefaults } from '../hooks/useLetterDefaults';
import { listCvs, type CvSummary } from '../services/cv';
import { saveCoverLetter } from '../services/coverLetter';
import { generateLetterPdf, shareLetterPdf } from '../services/coverLetterPdf';
import { coverLetterInputKey, coverLetterSavedKey } from '../utils/resultRef';
import {
  LETTER_PROFILS,
  LETTER_TONS,
  LETTER_TYPES,
  type CoverLetter,
  type CoverLetterParams,
  type LetterLangue,
  type OutputLanguage,
  type SavedCoverLetter,
  type LetterProfil,
  type LetterTon,
  type LetterType,
} from '../types';
import {
  colonneFormulaire,
  colors,
  formes,
  layout,
  radius,
  screenPadding,
  shadow,
  spacing,
  typography,
} from '../theme/tokens';
import { FEATURE_ACCENTS } from '../theme/featureAccents';
import { lireDocumentImporte, pickDocumentFile, type PickedFile } from '../services/mediaPicker';
import type { CvOfferInput } from '../services/claude';
import { t, tBrut } from '../i18n';
import { GenerationProgress } from '../components/GenerationProgress';
import { BLOC_LTR, TEXTE_LTR } from '../i18n/sens';
import { ContenuLtr } from '../components/ContenuLtr';

export interface CoverLetterCreateScreenProps {
  onBack?: () => void;
  // Aucun CV enregistré : on renvoie vers « Mon CV » plutôt que de laisser un formulaire inerte.
  onOpenMyCv?: () => void;
  // Lettre rouverte depuis « Mes lettres » : on ouvre directement sur le résultat, sans formulaire,
  // sans génération et sans nouvel enregistrement.
  initialLetter?: SavedCoverLetter;
}

// Libellés AFFICHÉS. Les CLÉS partent au serveur telles quelles — ce sont celles du prompt, elles
// ne se traduisent jamais. Construits au rendu : des constantes de module figeraient la langue.
export const profilLabel = (key: LetterProfil): string => t(`letterCreate.profils.${key}`);
export const typeLabel = (key: LetterType): string => t(`letterCreate.types.${key}`);
export const tonLabel = (key: LetterTon): string => t(`letterCreate.tons.${key}`);

const ACCENT = FEATURE_ACCENTS.letter;

// Le formulaire parle 'francais' | 'anglais' (vocabulaire hérité du prompt, il part tel quel au
// serveur) ; la bascule d'en-tête et la base parlent 'fr' | 'en'.
const versSortie = (l: LetterLangue): OutputLanguage => (l === 'anglais' ? 'en' : 'fr');
const versLangue = (o: OutputLanguage): LetterLangue => (o === 'en' ? 'anglais' : 'francais');

// Sélecteur en pastilles : tous les choix sont exposés d'un coup.
function ChipGroup<T extends string>({
  label,
  options,
  value,
  onChange,
  testID,
}: {
  label: string;
  options: readonly { key: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  testID: string;
}) {
  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <View style={styles.chips}>
        {options.map((o) => {
          const active = o.key === value;
          return (
            <Pressable
              key={o.key}
              testID={`${testID}-${o.key}`}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              onPress={() => onChange(o.key)}
              style={[styles.chip, active && styles.chipActive]}
            >
              <Text style={[styles.chipText, active && styles.chipTextActive]}>{o.label}</Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

function Field({
  label,
  value,
  onChangeText,
  placeholder,
  testID,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder: string;
  testID: string;
}) {
  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextInput
        testID={testID}
        style={styles.input}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={colors.grey500}
      />
    </View>
  );
}

type Phase = 'form' | 'result';

export function CoverLetterCreateScreen({
  onBack,
  onOpenMyCv,
  initialLetter,
}: CoverLetterCreateScreenProps) {
  const { generateCoverLetter, loading, error, refusProfil, reset } = useClaude();
  const { large } = useGabarit();
  // « Modifier l'annonce » ramène au champ : l'annonce est en haut, le refus tout en bas.
  const formulaire = useRef<ScrollView>(null);
  const annonceY = useRef(0);

  const [cvs, setCvs] = useState<CvSummary[] | null>(null);
  const [cvId, setCvId] = useState<string | null>(null);
  // La liste des CV ne s'ouvre qu'à la demande : la ligne du CV courant suffit presque toujours.
  const [changerCv, setChangerCv] = useState(false);
  const [annonce, setAnnonce] = useState('');
  // Import de l'annonce, sur le patron de `buildOffer` (CvScoreScreen) : les deux voies coexistent,
  // et une seule est active à la fois — choisir un fichier vide le texte, et réciproquement.
  const [annonceFile, setAnnonceFile] = useState<PickedFile | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // Profil et type de candidature : repris du compte quand il en a retenu (voir `useLetterDefaults`),
  // sinon présélectionnés comme avant. Ils restent dans « Plus d'options » et se changent là.
  const { defauts, memoriser } = useLetterDefaults();
  const [profil, setProfil] = useState<LetterProfil>('etudiant_jeune_diplome');
  const [type, setType] = useState<LetterType>('reponse_offre');
  // La lecture du compte arrive après l'ouverture : elle ne doit jamais écraser un choix déjà fait.
  const profilTouche = useRef(false);
  const typeTouche = useRef(false);
  useEffect(() => {
    if (!defauts) return;
    if (defauts.profil && !profilTouche.current) setProfil(defauts.profil);
    if (defauts.type && !typeTouche.current) setType(defauts.type);
  }, [defauts]);
  // La phrase « repris de ton compte » n'est vraie que tant que les deux choix affichés sont ceux du compte.
  const reprisDuCompte =
    !!defauts && defauts.profil !== null && defauts.type !== null && defauts.profil === profil && defauts.type === type;
  const [ton, setTon] = useState<LetterTon>('classique');
  const [langue, setLangue] = useState<LetterLangue>('francais');
  const [destinataireNom, setDestinataireNom] = useState('');
  const [destinataireFonction, setDestinataireFonction] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [metier, setMetier] = useState('');
  const [ville, setVille] = useState('');
  const [emailAccompagnement, setEmailAccompagnement] = useState(false);
  const [optionsOuvertes, setOptionsOuvertes] = useState(false);

  const [phase, setPhase] = useState<Phase>(initialLetter ? 'result' : 'form');
  const [letter, setLetter] = useState<CoverLetter | null>(initialLetter?.content ?? null);
  // Clé figée à la génération : l'avis porte sur l'entrée soumise, pas sur l'état du
  // formulaire au moment du vote.
  const [resultKey, setResultKey] = useState(
    initialLetter ? coverLetterSavedKey(initialLetter.id) : '',
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (initialLetter) return;
    listCvs()
      .then((rows) => {
        setCvs(rows);
        if (rows.length > 0) setCvId(rows[0].id);
      })
      .catch(() => setCvs([]));
  }, [initialLetter]);

  // Le bouton est dans le pied fixe : l'attente et le refus s'affichent en bas du défilement, on y
  // amène la page pour qu'on les voie sans chercher.
  useEffect(() => {
    if (loading || error) formulaire.current?.scrollToEnd?.({ animated: true });
  }, [loading, error]);

  const selectedCv = useMemo(() => (cvs ?? []).find((c) => c.id === cvId) ?? null, [cvs, cvId]);
  const chooseAnnonceFile = async () => {
    const outcome = await pickDocumentFile();
    if (outcome.error) return setFileError(outcome.error);
    if (!outcome.file) return;
    setFileError(null);
    setAnnonceFile(outcome.file);
    setAnnonce(''); // un seul mode actif à la fois
  };

  const buildAnnonce = async (): Promise<CvOfferInput> => {
    if (annonceFile) {
      const { payload, error } = await lireDocumentImporte(annonceFile);
      if (!payload) throw new Error(error);
      return payload;
    }
    return { text: annonce.trim() };
  };

  const canGenerate = !!selectedCv && (annonce.trim().length > 0 || annonceFile !== null);

  const generate = async () => {
    if (!selectedCv) return;
    setLocalError(null);
    setSaveError(null);

    const params: CoverLetterParams = {
      profil,
      typeCandidature: type,
      ton,
      langue,
      destinataireNom,
      destinataireFonction,
      organisation,
      metier,
      ville,
      emailAccompagnement,
    };

    // La clé de résultat dérive de l'ENTRÉE : le nom du fichier suffit à la distinguer d'un
    // collage, et il ne varie pas d'un appel à l'autre pour un même import.
    setResultKey(
      coverLetterInputKey({
        cvId: selectedCv.id,
        annonce: annonceFile ? `fichier:${annonceFile.name}` : annonce.trim(),
        ...params,
      }),
    );

    // La lecture de l'annonce importée est SORTIE de l'appel : dans l'objet littéral, son échec
    // interrompait `generate` avant tout affichage — le bouton ne faisait rien.
    let annoncePayload;
    try {
      annoncePayload = await buildAnnonce();
    } catch (e) {
      return setLocalError(e instanceof Error ? e.message : t('letterCreate.failed'));
    }

    const result = await generateCoverLetter({
      cv: JSON.stringify(selectedCv.content),
      annonce: annoncePayload,
      params,
    });
    if (!result) return; // quota ou erreur : le hook a déjà ouvert le Paywall ou posé `error`

    setLetter(result);
    setPhase('result');
    // Seuls les choix FAITS par la personne sont retenus : une valeur par défaut jamais touchée ne
    // doit pas devenir, au rappel suivant, un « repris de ton compte » qu'elle n'a pas décidé.
    memoriser({
      profil: profilTouche.current ? profil : undefined,
      type: typeTouche.current ? type : undefined,
    });

    // Enregistrement immédiat : la lettre a coûté un quota, elle ne doit pas dépendre de l'écran.
    try {
      await saveCoverLetter(
        result,
        result.objet || tBrut('letterCreate.defaultTitle'),
        // La base stocke 'fr' | 'en', partagé avec les autres features.
        versSortie(langue),
      );
    } catch {
      setSaveError(t('letterCreate.saveFailed'));
    }
  };

  const exportPdf = async () => {
    if (!letter) return;
    setExporting(true);
    try {
      // Langue du DOCUMENT, pas de l'interface : la typographie française ne s'applique qu'aux
      // lettres françaises.
      await shareLetterPdf(await generateLetterPdf(letter, versSortie(langue)));
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : t('letterCreate.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  // ───────────── Aucun CV : on n'affiche pas un formulaire inutilisable ─────────────
  if (!initialLetter && cvs !== null && cvs.length === 0) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <ScreenHeader onBack={onBack} testID="letter-back" />
        <View style={styles.emptyHeader}>
          <FeatureHeader
            icon={Mail}
            accent="letter"
            title={t('letterCreate.title')}
            subtitle={t('letterCreate.subtitle')}
            testID="letter-header"
          />
        </View>
        <View style={styles.emptyWrap}>
          <Text style={styles.emptyTitle}>{t('letterCreate.noCvTitle')}</Text>
          <Text style={styles.emptyText} testID="letter-no-cv">
            {t('letterCreate.noCvText')}
          </Text>
          {onOpenMyCv ? (
            <GradientButton label={t('letterCreate.goToCv')} onPress={onOpenMyCv} withArrow testID="letter-go-cv" />
          ) : null}
        </View>
      </SafeAreaView>
    );
  }

  // ───────────── Résultat ─────────────
  if (phase === 'result' && letter) {
  const rEntete = (
    <>
          <FeatureHeader
            icon={Mail}
            accent="letter"
            title={t('letterCreate.resultTitle')}
            subtitle={t('letterCreate.resultSubtitle')}
            testID="letter-header"
          />
          <ContenuLtr>
            <Text style={styles.resultObjet} testID="letter-objet">
              {letter.objet}
            </Text>
          </ContenuLtr>
          <Text style={styles.resultMeta} testID="letter-meta">
            {t('letterCreate.wordCount', { count: letter.nombre_mots })}
          </Text>

              </>
  );
  const rConfirm = (
    <>
{letter.informations_a_confirmer.length > 0 ? (
            <Card testID="letter-to-confirm">
              <Text style={styles.confirmTitle}>{t('letterCreate.toConfirm')}</Text>
              {letter.informations_a_confirmer.map((q, i) => (
                <Text key={i} style={styles.confirmItem}>
                  • {q}
                </Text>
              ))}
            </Card>
          ) : null}

              </>
  );
  const rApercu = (
    <>
<Card testID="letter-preview" style={BLOC_LTR}>
            <ContenuLtr>
              <Text style={styles.letterAppel}>{letter.lettre.formule_appel}</Text>
              {letter.lettre.paragraphes.map((p, i) => (
                <Text key={i} style={styles.letterPara}>
                  {p}
                </Text>
              ))}
              <Text style={styles.letterPara}>{letter.lettre.formule_politesse}</Text>
              <Text style={styles.letterSignature}>{letter.lettre.signature}</Text>
            </ContenuLtr>
          </Card>

              </>
  );
  const rEmail = (
    <>
{letter.email_accompagnement ? (
            <Card testID="letter-email" style={BLOC_LTR}>
              <ContenuLtr>
                <Text style={styles.confirmTitle}>{tBrut('letterCreate.emailBlock')}</Text>
                <Text style={styles.letterPara}>Objet : {letter.email_accompagnement.objet}</Text>
                <Text style={styles.letterPara}>{letter.email_accompagnement.corps}</Text>
              </ContenuLtr>
            </Card>
          ) : null}

              </>
  );
  const rErreurs = (
    <>
{saveError ? (
            <Text testID="letter-save-error" style={styles.error}>
              {saveError}
            </Text>
          ) : null}
          {localError ? (
            <Text testID="letter-export-error" style={styles.error}>
              {localError}
            </Text>
          ) : null}

              </>
  );
  const rActions = (
    <>
<GradientButton
            label={t('letterCreate.download')}
            onPress={() => void exportPdf()}
            loading={exporting}
            withArrow
            testID="letter-download"
          />

          <FeedbackWidget
            feature="cover_letter"
            resultKey={resultKey}
            testID="letter-feedback"
          />
    </>
  );

    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <ScreenHeader onBack={onBack} testID="letter-back" />
        {/* Ordinateur : la lettre à gauche, sa lecture et ses actions à droite. Un résultat peut défiler. */}
        {large ? (
          <ScrollView testID="letter-resultat-colonnes" contentContainerStyle={styles.colonnesResultat} showsVerticalScrollIndicator={false}>
            <View style={styles.colApercu}>{rApercu}</View>
            <View style={styles.colDroiteLetre}>
              {rEntete}
              {rConfirm}
              {rEmail}
              {rErreurs}
              {rActions}
            </View>
          </ScrollView>
        ) : (
        <ScrollView contentContainerStyle={[styles.content, colonneFormulaire]} showsVerticalScrollIndicator={false}>
          {rEntete}
          {rConfirm}
          {rApercu}
          {rEmail}
          {rErreurs}
          {rActions}
        </ScrollView>
        )}
      </SafeAreaView>
    );
  }

  // ───────────── Formulaire ─────────────
  const plusieursCv = (cvs?.length ?? 0) > 1;
  const erreurPied = localError ?? (error && !refusProfil ? error : null);

  const sCv = (
    <>
          {/* ─── CV utilisé ─── */}
          <Text style={styles.etiquette}>{t('letterCreate.cvUsed')}</Text>
          <View style={styles.carte}>
            {selectedCv ? (
              <View testID="letter-cv-courant" style={styles.cvLigne}>
                <View style={styles.cvIcone}>
                  <FileText size={18} color={ACCENT.ink} strokeWidth={2} />
                </View>
                <Text style={styles.cvNom} numberOfLines={1}>
                  {selectedCv.title}
                </Text>
                {plusieursCv ? (
                  <Pressable
                    testID="letter-cv-change"
                    accessibilityRole="button"
                    accessibilityLabel={t('letterCreate.changeA11y')}
                    accessibilityState={{ expanded: changerCv }}
                    onPress={() => setChangerCv((v) => !v)}
                    style={styles.changer}
                  >
                    <Text style={styles.changerTexte}>{t('letterCreate.change')}</Text>
                  </Pressable>
                ) : null}
              </View>
            ) : null}

            {changerCv
              ? cvs?.map((cv) => {
                  const active = cv.id === cvId;
                  return (
                    <Pressable
                      key={cv.id}
                      testID={`letter-cv-${cv.id}`}
                      accessibilityRole="button"
                      accessibilityState={{ selected: active }}
                      onPress={() => {
                        setCvId(cv.id);
                        setChangerCv(false);
                      }}
                      style={[styles.cvLigne, styles.cvLigneListe]}
                    >
                      <Text style={styles.cvNom} numberOfLines={1}>
                        {cv.title}
                      </Text>
                      <View style={[styles.radio, active && styles.radioOn]}>
                        {active ? <Check size={12} color={colors.white} strokeWidth={3} /> : null}
                      </View>
                    </Pressable>
                  );
                })
              : null}
          </View>

              </>
  );
  const sAnnonce = (
    <>
{/* ─── Annonce visée ─── */}
          <View style={[styles.bloc, large && styles.blocL]} onLayout={(e) => (annonceY.current = e.nativeEvent.layout.y)}>
            <Text style={styles.etiquette}>{t('letterCreate.annonce')}</Text>
            <View style={[styles.carte, large && styles.carteAnnonceL]}>
              <TextInput
                testID="letter-annonce"
                style={[styles.champAnnonce, large && styles.champAnnonceL]}
                value={annonce}
                onChangeText={(v) => {
                  setAnnonce(v);
                  if (v.trim()) setAnnonceFile(null);
                }}
                placeholder={t('letterCreate.annoncePlaceholder')}
                placeholderTextColor={colors.grey500}
                multiline
              />

              {/* Le collage RESTE, l'import s'y ajoute : les deux voies coexistent, comme sur
                  l'écran de notation de CV. Un seul mode est actif à la fois — importer vide le
                  champ, et saisir du texte retire le fichier. */}
              <View style={styles.separateur} />
              <View style={styles.importRow}>
                <Text style={styles.importOr}>{t('letterCreate.annonceOr')}</Text>
                <Pressable
                  testID="letter-annonce-file"
                  accessibilityRole="button"
                  onPress={() => void chooseAnnonceFile()}
                  style={styles.importButton}
                >
                  <Upload size={16} color={ACCENT.ink} strokeWidth={2.2} />
                  <Text style={styles.importButtonText}>{t('letterCreate.annonceFile')}</Text>
                </Pressable>
              </View>

              {annonceFile ? (
                <View style={styles.importPicked} testID="letter-annonce-file-picked">
                  <Text style={styles.importPickedText}>
                    {t('letterCreate.annonceFilePicked', { name: annonceFile.name })}
                  </Text>
                  <Pressable
                    testID="letter-annonce-file-remove"
                    accessibilityRole="button"
                    onPress={() => setAnnonceFile(null)}
                    hitSlop={8}
                  >
                    <Text style={styles.importRemove}>{t('letterCreate.annonceFileRemove')}</Text>
                  </Pressable>
                </View>
              ) : null}

              {fileError ? (
                <Text style={styles.importError} testID="letter-annonce-file-error">
                  {fileError}
                </Text>
              ) : null}
            </View>
          </View>

              </>
  );
  const sTon = (
    <>
{/* ─── Ton de la lettre : six pastilles en grille de trois colonnes ─── */}
          <Text style={styles.etiquette}>{t('letterCreate.ton')}</Text>
          <View style={styles.grilleTons}>
            {LETTER_TONS.map((k) => {
              const active = k === ton;
              return (
                <Pressable
                  key={k}
                  testID={`letter-ton-${k}`}
                  accessibilityRole="button"
                  accessibilityState={{ selected: active }}
                  onPress={() => setTon(k)}
                  style={[styles.ton, active && styles.tonActif]}
                >
                  <Text
                    style={[styles.tonTexte, active && styles.tonTexteActif]}
                    numberOfLines={1}
                    adjustsFontSizeToFit
                    minimumFontScale={0.8}
                  >
                    {tonLabel(k)}
                  </Text>
                </Pressable>
              );
            })}
          </View>

              </>
  );
  const sOptions = (
    <>
{/* ─── Plus d'options : repliée, tout y est facultatif sauf ce qui a une valeur par défaut ─── */}
          <SectionRepliable
            testID="letter-options"
            titre={t('letterCreate.optionsTitre')}
            sousTitre={t('letterCreate.optionsSousTitre')}
            icone={<SlidersHorizontal size={18} color={colors.electricDeep} strokeWidth={2.2} />}
            indicateur="plus"
            ouverte={optionsOuvertes}
            onToggle={() => setOptionsOuvertes((v) => !v)}
          >
            <ChipGroup
              label={t('letterCreate.profil')}
              options={LETTER_PROFILS.map((k) => ({ key: k, label: profilLabel(k) }))}
              value={profil}
              onChange={(k) => {
                profilTouche.current = true;
                setProfil(k);
              }}
              testID="letter-profil"
            />
            <ChipGroup
              label={t('letterCreate.type')}
              options={LETTER_TYPES.map((k) => ({ key: k, label: typeLabel(k) }))}
              value={type}
              onChange={(k) => {
                typeTouche.current = true;
                setType(k);
              }}
              testID="letter-type"
            />
            {reprisDuCompte ? (
              <Text style={styles.reprisDuCompte} testID="letter-repris-compte">
                {t('letterCreate.reprisDuCompte')}
              </Text>
            ) : null}
            <Field
              label={t('letterCreate.organisation')}
              value={organisation}
              onChangeText={setOrganisation}
              placeholder={t('letterCreate.organisationPlaceholder')}
              testID="letter-organisation"
            />
            <Field
              label={t('letterCreate.metier')}
              value={metier}
              onChangeText={setMetier}
              placeholder={t('letterCreate.metierPlaceholder')}
              testID="letter-metier"
            />
            <Field
              label={t('letterCreate.recipientName')}
              value={destinataireNom}
              onChangeText={setDestinataireNom}
              placeholder={t('letterCreate.recipientNamePlaceholder')}
              testID="letter-destinataire-nom"
            />
            <Field
              label={t('letterCreate.recipientRole')}
              value={destinataireFonction}
              onChangeText={setDestinataireFonction}
              placeholder={t('letterCreate.recipientRolePlaceholder')}
              testID="letter-destinataire-fonction"
            />
            <Field
              label={t('letterCreate.city')}
              value={ville}
              onChangeText={setVille}
              placeholder={t('letterCreate.cityPlaceholder')}
              testID="letter-ville"
            />

            <Pressable
              testID="letter-email-toggle"
              accessibilityRole="checkbox"
              accessibilityState={{ checked: emailAccompagnement }}
              onPress={() => setEmailAccompagnement((v) => !v)}
              style={styles.toggle}
            >
              <View style={[styles.box, emailAccompagnement && styles.boxChecked]}>
                {emailAccompagnement ? <Check size={14} color={colors.white} strokeWidth={3} /> : null}
              </View>
              <Text style={styles.toggleLabel}>{t('letterCreate.addEmail')}</Text>
            </Pressable>
          </SectionRepliable>

              </>
  );
  const sRefus = (
    <>
{/* Offre hors de portée : rien n'a été décompté, et le formulaire est resté rempli. La
              sortie utile est de changer d'annonce, pas de relancer la même. */}
          {error && refusProfil ? (
            <View testID="letter-trop-eloigne" style={styles.refus}>
              <Text style={styles.refusMessage}>{error}</Text>
              {refusProfil.raison ? <Text style={styles.refusRaison}>{refusProfil.raison}</Text> : null}
              <Button
                title={t('letterCreate.modifierAnnonce')}
                variant="secondary"
                testID="letter-modifier-annonce"
                onPress={() => {
                  reset();
                  formulaire.current?.scrollTo({ y: annonceY.current, animated: true });
                }}
              />
            </View>
          ) : null}

              </>
  );
  const sCharge = (
    <>
{loading ? <GenerationProgress messageKey="letter" testID="letter-progress" /> : null}
    </>
  );
  const sPied = (
    <>
      {/* Un échec simple s'affiche ICI, à côté du bouton qui l'a provoqué. */}
      <PiedAction testID="letter-actions" enColonne={large}>
        {erreurPied ? (
          <Text testID={localError ? 'letter-offer-error' : 'letter-error'} style={styles.erreurPied}>
            {erreurPied}
          </Text>
        ) : null}
        {!erreurPied && !canGenerate && !loading ? (
          <Text testID="letter-hint" style={styles.indice}>
            {t('letterCreate.hint')}
          </Text>
        ) : null}
        <GradientButton
          variante="cta"
          hauteur="lg"
          label={t('letterCreate.generate')}
          onPress={() => void generate()}
          loading={loading}
          disabled={!canGenerate}
          withArrow
          testID="letter-generate"
        />
      </PiedAction>
    </>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <View testID="letter-header">
        <ScreenHeader
          variante="carte"
          onBack={onBack}
          testID="letter-back"
          title={t('letterCreate.title')}
          droite={
            <View style={styles.langue}>
              <OutputLanguagePicker
                variante="compact"
                value={versSortie(langue)}
                onChange={(v) => setLangue(versLangue(v))}
                label={t('letterCreate.langue')}
                testID="letter-langue"
              />
            </View>
          }
        />
      </View>

      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        {/* Ordinateur : le CV et l'annonce (qui prend la hauteur) à gauche, le ton, les options et le
            lancement à droite. Tout tient dans la fenêtre ; le téléphone garde sa pile. */}
        {large ? (
          <ScrollView
            ref={formulaire}
            testID="letter-colonnes"
            contentContainerStyle={styles.colonnesLetre}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.colGaucheLetre}>
              {sCv}
              {sAnnonce}
            </View>
            <View style={styles.colDroiteLetre}>
              {sTon}
              {sOptions}
              {sRefus}
              {sCharge}
              <View style={styles.piedLetre}>{sPied}</View>
            </View>
          </ScrollView>
        ) : (
        <ScrollView
          ref={formulaire}
          contentContainerStyle={[styles.content, colonneFormulaire]}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          showsVerticalScrollIndicator={false}
        >
          {sCv}
          {sAnnonce}
          {sTon}
          {sOptions}
          {sRefus}
          {sCharge}
        </ScrollView>
        )}
      </KeyboardAvoidingView>

      {large ? null : sPied}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  colonnesLetre: {
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: spacing.xl,
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    padding: spacing.xxl + spacing.xl,
  },
  colonnesResultat: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xl,
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    padding: spacing.xxl + spacing.xl,
  },
  colGaucheLetre: { flex: 1, minWidth: 0, gap: spacing.md },
  colDroiteLetre: { flex: 1, minWidth: 0, gap: spacing.md },
  colApercu: { flex: 1.25, minWidth: 0 },
  piedLetre: { marginTop: 'auto' },
  blocL: { flex: 1 },
  carteAnnonceL: { flex: 1 },
  champAnnonceL: { flex: 1, minHeight: 160 },
  reprisDuCompte: { fontSize: typography.small.size, color: colors.grey500, lineHeight: 17 },
  container: { flex: 1, backgroundColor: colors.grey50 },
  flex: { flex: 1 },
  content: { padding: screenPadding, gap: spacing.md, paddingBottom: spacing.xxl },
  bloc: { gap: spacing.md },
  langue: { marginStart: 'auto' },

  etiquette: {
    fontSize: typography.caption.size + 1,
    fontWeight: '800',
    letterSpacing: typography.caption.letterSpacing + 0.2,
    textTransform: 'uppercase',
    color: colors.grey500,
  },
  carte: {
    borderRadius: formes.rayon.lg,
    backgroundColor: colors.white,
    padding: spacing.md,
    gap: spacing.sm,
    ...shadow.card,
  },

  // CV : la ligne du CV utilisé, puis la liste quand on touche « Changer ».
  cvLigne: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, minHeight: formes.cible },
  cvLigneListe: { borderTopWidth: 1, borderTopColor: colors.grey200, paddingTop: spacing.sm },
  cvIcone: {
    width: 36,
    height: 36,
    borderRadius: formes.rayon.sm,
    backgroundColor: ACCENT.tint,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cvNom: { flex: 1, fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.night },
  // ENCRE et non accent : le magenta de la lettre n'affiche que 3,66:1 sur blanc, sous les 4,5
  // exigés pour un texte courant. L'encre est la même identité, assombrie jusqu'à devenir lisible.
  changer: {
    minHeight: formes.cible,
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    borderRadius: formes.rayon.sm,
    backgroundColor: ACCENT.tint,
  },
  changerTexte: { fontSize: typography.small.size, fontWeight: '800', color: ACCENT.ink },
  radio: {
    width: 22,
    height: 22,
    borderRadius: radius.pill,
    borderWidth: 2,
    borderColor: colors.grey400,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOn: { backgroundColor: ACCENT.ink, borderColor: ACCENT.ink },

  // Annonce : champ de saisie dans la carte, puis l'import de fichier sous un filet.
  champAnnonce: {
    minHeight: 112,
    borderWidth: 1.5,
    borderColor: colors.grey200,
    borderRadius: radius.lg,
    padding: spacing.md,
    fontSize: typography.body.size,
    fontWeight: '500',
    color: colors.night,
    backgroundColor: colors.grey50,
    textAlignVertical: 'top',
  },
  separateur: { height: 1, backgroundColor: colors.grey200, marginVertical: spacing.xs },
  importRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' },
  importOr: { flex: 1, fontSize: typography.small.size, color: colors.grey500 },
  importButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    minHeight: formes.cible,
    paddingHorizontal: spacing.md,
    borderRadius: formes.rayon.sm,
    backgroundColor: ACCENT.tint,
  },
  importButtonText: { fontSize: typography.small.size, fontWeight: '800', color: ACCENT.ink },
  importPicked: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
  importPickedText: { flex: 1, fontSize: typography.small.size, color: colors.grey700 },
  importRemove: { fontSize: typography.small.size, fontWeight: '800', color: ACCENT.ink },
  importError: { fontSize: typography.small.size, color: colors.error },

  // Ton : trois colonnes, deux rangées.
  grilleTons: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  ton: {
    flexBasis: '30%',
    flexGrow: 1,
    minHeight: formes.cible,
    paddingHorizontal: spacing.sm,
    borderRadius: formes.rayon.sm,
    backgroundColor: colors.white,
    borderWidth: 1.5,
    borderColor: colors.grey200,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Fond en encre : le blanc sur l'accent clair du magenta ne tient pas 4,5:1.
  tonActif: { backgroundColor: ACCENT.ink, borderColor: ACCENT.ink },
  tonTexte: { fontSize: typography.small.size, fontWeight: '700', color: colors.grey700 },
  tonTexteActif: { color: colors.white, fontWeight: '800' },

  // Champs de « Plus d'options ».
  field: { gap: spacing.xs },
  fieldLabel: { fontSize: typography.small.size, fontWeight: '700', color: colors.grey700 },
  input: {
    minHeight: formes.hauteur.md,
    borderWidth: 1.5,
    borderColor: colors.grey200,
    borderRadius: radius.lg,
    backgroundColor: colors.grey50,
    paddingHorizontal: spacing.md,
    fontSize: typography.body.size,
    fontWeight: '500',
    color: colors.night,
  },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  chip: {
    minHeight: formes.hauteur.sm,
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    borderRadius: radius.pill,
    backgroundColor: colors.white,
    borderWidth: 1.5,
    borderColor: colors.grey200,
    maxWidth: '100%',
  },
  chipActive: { backgroundColor: ACCENT.ink, borderColor: ACCENT.ink },
  chipText: { fontSize: typography.small.size, color: colors.grey700 },
  chipTextActive: { color: colors.white, fontWeight: '800' },

  toggle: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, minHeight: formes.cible },
  box: {
    width: 22,
    height: 22,
    borderRadius: radius.sm,
    borderWidth: 2,
    borderColor: colors.grey400,
    alignItems: 'center',
    justifyContent: 'center',
  },
  boxChecked: { backgroundColor: ACCENT.ink, borderColor: ACCENT.ink },
  toggleLabel: { flex: 1, fontSize: typography.small.size, color: colors.grey700 },

  // Pied d'action.
  indice: { fontSize: typography.small.size, color: colors.grey500, textAlign: 'center' },
  erreurPied: { fontSize: typography.small.size, color: colors.error, textAlign: 'center' },
  error: { fontSize: typography.small.size, color: colors.error },
  refus: { gap: spacing.sm, padding: spacing.lg, borderRadius: radius.lg, backgroundColor: colors.corailSoft },
  refusMessage: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.night },
  refusRaison: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.night },

  // ── Résultat ──
  resultObjet: { ...TEXTE_LTR, fontSize: typography.h2.size, fontWeight: typography.h2.weight, color: colors.night },
  resultMeta: { fontSize: typography.small.size, color: colors.grey500 },
  confirmTitle: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.night, marginBottom: spacing.xs },
  confirmItem: { fontSize: typography.small.size, lineHeight: typography.body.lineHeight, color: colors.grey700 },
  letterAppel: { ...TEXTE_LTR, fontSize: typography.body.size, color: colors.grey900, marginBottom: spacing.sm },
  letterPara: { ...TEXTE_LTR,
    fontSize: typography.body.size,
    lineHeight: typography.body.lineHeight,
    color: colors.grey700,
    marginBottom: spacing.sm,
  },
  letterSignature: { ...TEXTE_LTR, fontSize: typography.body.size, fontWeight: '700', color: colors.grey900 },

  emptyHeader: { paddingHorizontal: screenPadding, paddingTop: spacing.lg },
  emptyWrap: { flex: 1, justifyContent: 'center', padding: screenPadding, gap: spacing.md },
  emptyTitle: { fontSize: typography.h2.size, fontWeight: typography.h2.weight, color: colors.night, textAlign: 'center' },
  emptyText: {
    fontSize: typography.body.size,
    lineHeight: typography.body.lineHeight,
    color: colors.grey500,
    textAlign: 'center',
  },
});
